/**
 * The deck — every card in one 3D stack, the dashboard's signature moment.
 *
 * The cards fly in on load, then the stack cycles one card to the top every few
 * seconds so the wallet reads as a physical object rather than a grid. Clicking
 * a card (or its dot) brings it forward and stops the cycling; the focused card's
 * facts sit under the stack, and its name links to the card's page.
 *
 * Auto-cycling is off under prefers-reduced-motion, and pauses while the pointer
 * or keyboard focus is inside the stack — nothing moves under the user's hand.
 */

import { useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { CardObject } from "@/components/cards/CardObject";
import { urgencyLabel } from "@/components/dashboard/DueRunway";
import type { CardSummary } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { daysUntil, formatDate, formatMoney } from "@/lib/format";
import { summaryToCard } from "@/components/dashboard/summaryToCard";

const VISIBLE = 8;
const CYCLE_MS = 4200;

export function WalletStack({ cards }: { cards: CardSummary[] }) {
  const reduce = useReducedMotion();
  const [current, setCurrent] = React.useState(0);
  const [entered, setEntered] = React.useState(false);
  const [paused, setPaused] = React.useState(false);
  const n = cards.length;

  React.useEffect(() => {
    const t = setTimeout(() => setEntered(true), 120);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    if (reduce || paused || n < 2) return;
    const t = setInterval(() => setCurrent((c) => (c + 1) % n), CYCLE_MS);
    return () => clearInterval(t);
  }, [reduce, paused, n]);

  const pick = (i: number) => {
    setCurrent(i);
    setPaused(true);
  };

  const focused = cards[current] ?? cards[0];
  if (!focused) return null;
  const shown = Math.min(n, VISIBLE);

  return (
    <div
      className="flex h-full flex-col"
      onPointerEnter={() => setPaused(true)}
      onFocusCapture={() => setPaused(true)}
    >
      <div className="flex items-center justify-between">
        <span className="label-micro">
          Your deck · {n} card{n === 1 ? "" : "s"}
        </span>
        {n > 1 && (
          <div className="flex items-center gap-1.5" role="tablist" aria-label="Choose a card">
            {cards.map((c, i) => (
              <button
                key={c.card_id}
                type="button"
                role="tab"
                aria-selected={i === current}
                aria-label={c.nickname ?? c.product_name ?? c.issuer}
                onClick={() => pick(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-500 ease-out",
                  i === current ? "w-5 bg-brass" : "w-1.5 bg-ink/20 hover:bg-ink/40",
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* The stage. Perspective lives here; the deck itself is tilted back and
          turned, and each card is pushed along Z by its distance from the top. */}
      <div className="relative mb-6 mt-2 flex min-h-[16rem] flex-1 items-center justify-center [perspective:1600px] sm:min-h-[21rem]">
        <div
          className="relative aspect-card w-[min(22rem,78%)] transition-transform duration-1000 ease-out [transform-style:preserve-3d]"
          style={{
            transform: reduce
              ? "rotateX(40deg) rotateZ(-20deg)"
              : "rotateX(50deg) rotateZ(-26deg) translateY(-16%)",
          }}
        >
          {cards.map((c, i) => {
            const k = (i - current + n) % n;
            const hidden = k >= shown;
            const top = k === 0;
            const z = top ? shown * 14 + 34 : (shown - k) * 14;
            const y = top ? -30 : k * 24;
            return (
              <button
                key={c.card_id}
                type="button"
                onClick={() => pick(i)}
                aria-label={`Show ${c.nickname ?? c.product_name ?? c.issuer}`}
                tabIndex={-1}
                className="absolute inset-0 block rounded-card text-left [transform-style:preserve-3d] focus:outline-none"
                style={{
                  zIndex: 100 - k,
                  opacity: entered && !hidden ? 1 : 0,
                  filter: top ? "none" : `brightness(${Math.max(0.45, 1 - k * 0.08)})`,
                  transform: entered
                    ? `translateZ(${z}px) translateY(${y}px)`
                    : `translateZ(0px) translateY(${260 + i * 36}px)`,
                  transition: `transform 1.1s cubic-bezier(0.16,1,0.3,1) ${entered ? 0 : i * 0.05}s, opacity 0.6s ease ${entered ? 0 : i * 0.05}s, filter 0.8s ease`,
                }}
              >
                <CardObject card={summaryToCard(c)} outstanding={c.outstanding} showFacts={false} />
              </button>
            );
          })}
        </div>
      </div>

      {/* The focused card's facts. Keyed so they fade when the card changes. */}
      <div
        key={focused.card_id}
        className="animate-rise space-y-4"
        style={{ animationDuration: "0.6s" }}
      >
        <div className="min-w-0">
          <div className="label-micro truncate">{focused.issuer}</div>
          <Link
            to={`/cards/${focused.card_id}`}
            className="group mt-1 inline-flex max-w-full items-baseline gap-2 text-2xl font-medium tracking-[-0.035em] text-ink"
          >
            <span className="truncate">
              {focused.nickname ?? focused.product_name ?? focused.issuer}
            </span>
            <span className="accent-serif text-xl text-muted">•{focused.last4}</span>
            <ArrowUpRight className="h-4 w-4 self-center text-faint transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink" />
          </Link>
        </div>
        <dl className="grid grid-cols-3 gap-4 border-t border-hairline pt-4">
          <Fact label="Outstanding" value={focused.outstanding !== null ? formatMoney(focused.outstanding) : "—"} />
          <Fact
            label="Due"
            value={focused.due_date ? formatDate(focused.due_date).slice(0, 6) : "—"}
            note={
              focused.due_date
                ? urgencyLabel(focused.urgency, daysUntil(focused.due_date))
                : focused.statement_count === 0
                  ? "No statement yet"
                  : undefined
            }
            tone={
              focused.urgency === "overdue"
                ? "text-overdue"
                : focused.urgency === "soon" || focused.urgency === "unconfirmed"
                  ? "text-warn"
                  : focused.urgency === "paid"
                    ? "text-paid"
                    : undefined
            }
          />
          <Fact
            label="Available"
            value={focused.available_limit !== null ? formatMoney(focused.available_limit) : "—"}
          />
        </dl>
      </div>
    </div>
  );
}

function Fact({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-1 text-[17px] font-medium tracking-[-0.02em] text-ink">{value}</dd>
      {note && <dd className={cn("mt-0.5 text-[11.5px] text-faint", tone)}>{note}</dd>}
    </div>
  );
}
